import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Carte, Erreur } from '../../composants/communs.tsx';
import type { BoutiqueBreve, Cree } from './types.ts';

/**
 * Création d'un compte — RG-161, RG-162.
 *
 * Les boutiques proposées sont celles de l'appelant, jamais davantage : on
 * n'accorde pas une portée qu'on ne détient pas soi-même. Le serveur le
 * refuserait de toute façon, mais proposer une case qui mène à un 403 serait
 * une invitation à l'erreur.
 */
export function FormulaireCompte({
  boutiqueParDefaut,
  boutiques,
  onCree,
}: {
  boutiqueParDefaut: string | null;
  boutiques: BoutiqueBreve[];
  onCree: (resultat: Cree) => void;
}): ReactNode {
  const [identifiant, setIdentifiant] = useState('');
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [roleCode, setRoleCode] = useState('VEN');
  const [choisies, setChoisies] = useState<string[]>(
    boutiqueParDefaut === null ? [] : [boutiqueParDefaut],
  );

  const mutation = useMutation({
    mutationFn: (corps: Record<string, unknown>) =>
      appeler<Cree>('/utilisateurs', { methode: 'POST', corps }),
    onSuccess: onCree,
  });

  function soumettre(evenement: SyntheticEvent): void {
    evenement.preventDefault();
    mutation.mutate({
      id: crypto.randomUUID(),
      identifiant: identifiant.trim(),
      nom: nom.trim(),
      telephone: telephone.trim(),
      role_code: roleCode,
      boutiques: choisies,
    });
  }

  return (
    <Carte titre="Nouveau compte">
      <form onSubmit={soumettre}>
        <div className="champs">
          <div className="champ">
            <label htmlFor="identifiant-compte">Identifiant</label>
            <input
              id="identifiant-compte"
              name="identifiant"
              required
              minLength={3}
              value={identifiant}
              onChange={(evenement) => {
                setIdentifiant(evenement.target.value);
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="nom-compte">Nom</label>
            <input
              id="nom-compte"
              name="nom"
              required
              value={nom}
              onChange={(evenement) => {
                setNom(evenement.target.value);
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="telephone-compte">Téléphone</label>
            <input
              id="telephone-compte"
              name="telephone"
              required
              placeholder="+229…"
              value={telephone}
              onChange={(evenement) => {
                setTelephone(evenement.target.value);
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="role-compte">Rôle</label>
            <select
              id="role-compte"
              value={roleCode}
              onChange={(evenement) => {
                setRoleCode(evenement.target.value);
              }}
            >
              <option value="VEN">Vendeur</option>
              <option value="ADM">Administrateur</option>
            </select>
          </div>
        </div>

        <fieldset style={{ border: 0, padding: 0, margin: '10px 0' }}>
          <legend className="indicateur__libelle">
            Boutiques accessibles
          </legend>
          <div className="ligne" style={{ marginTop: 8 }}>
            {boutiques.map((boutique) => (
              <label key={boutique.id} style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto', marginRight: 6 }}
                  checked={choisies.includes(boutique.id)}
                  onChange={() => {
                    setChoisies((actuelles) =>
                      actuelles.includes(boutique.id)
                        ? actuelles.filter((id) => id !== boutique.id)
                        : [...actuelles, boutique.id],
                    );
                  }}
                />
                {boutique.code}
              </label>
            ))}
          </div>
        </fieldset>

        <Erreur erreur={mutation.error} />

        <button type="submit" disabled={mutation.isPending || choisies.length === 0}>
          {mutation.isPending ? 'Création…' : 'Créer le compte'}
        </button>
      </form>
    </Carte>
  );
}
